import { useAppConfiguration } from "@/api/app-configuration.api";
import { AlertModal } from "@/components/generic/AlertModal";
import { Button } from "@/components/generic/Button";
import { SettingsContainer } from "@/components/layout/SettingsContainer";
import {
  FilterGroup,
  FilterIdentifier,
  getFilterGroupById,
} from "@/data/filters/config";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import type {
  Configuration,
  DepartmentSizeQueryFilter,
  EventQueryFilter,
  QueryFilter,
} from "@/types/campaign";
import {
  buildFilterGroupFromLabelInfo,
  updateFilter,
} from "@/utils/filter-utils";
import { useBlocker, type ToOptions } from "@tanstack/react-router";
import {
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
  type Dispatch,
} from "react";
import { twMerge } from "tailwind-merge";

type Props = {
  configuration: Configuration;
  setConfiguration: Dispatch<SetStateAction<Configuration>>;
  onUpdated: () => void;
  isNewCampaign: boolean;
  backLinkOptions: ToOptions;
  hasBorder?: boolean;
  filterGroupId: string;
  hideCancelButton: boolean;
};

export function EditFilter({
  configuration,
  setConfiguration,
  onUpdated,
  isNewCampaign,
  backLinkOptions,
  hasBorder = true,
  filterGroupId,
  hideCancelButton,
}: Props) {
  const { data: appConfig } = useAppConfiguration();
  const [showButtons, setShowButtons] = useState(true);
  const [isDirty, setIsDirty] = useState(false);

  const isIntentionalNavRef = useRef(false);

  const blocker = useBlocker({
    shouldBlockFn: () => {
      if (isIntentionalNavRef.current) return false;
      return isDirty;
    },
    withResolver: true,
  });

  const filterGroup = useMemo(() => {
    const result = getFilterGroupById(filterGroupId);
    if (result) return result;
    // If no filter group is found, try to build it from label info
    if (appConfig?.labelFilters) {
      const filterGroups = buildFilterGroupFromLabelInfo(
        appConfig?.labelFilters,
      );
      const foundFilterGroup = filterGroups.find(
        (fG) => fG.id === filterGroupId,
      );
      if (foundFilterGroup) {
        return {
          group: foundFilterGroup,
          category: {
            id: "custom",
            key: "custom",
          },
        };
      }
    }
  }, [filterGroupId, appConfig?.labelFilters]);

  if (!filterGroup) {
    throw new Error(`Filter group ${filterGroupId} not found`);
  }

  const { group } = filterGroup;

  const formId = useMemo(() => `filter-form-${filterGroupId}`, [filterGroupId]);

  const { filterIdentifiers, filterIdsByFilterName } = useMemo(() => {
    const filterIds: readonly string[] = group.filterType.filters;
    const groupFilters: Partial<Record<string, FilterIdentifier | null>> =
      group.filters;
    return {
      filterIdentifiers: new Map(
        filterIds.map((id) => [id, groupFilters[id] || null]),
      ),
      filterIdsByFilterName: new Map(
        filterIds.map((id) => [groupFilters[id]?.filterName || null, id]),
      ),
    };
  }, [group]);

  const queryFilters = useMemo(() => {
    const queryFilters: Partial<
      Record<string, QueryFilter | EventQueryFilter | DepartmentSizeQueryFilter>
    > = {};
    for (const queryFilter of configuration?.query.filters || []) {
      const filterId = filterIdsByFilterName.get(queryFilter.filterName);
      if (filterId) {
        queryFilters[filterId] = queryFilter;
      }
    }
    return queryFilters;
  }, [filterIdsByFilterName, configuration?.query.filters]);

  const hasValue = useMemo(() => {
    const values = Object.values(queryFilters);
    return values.length > 0 && values.some((v) => v !== undefined);
  }, [queryFilters]);

  const setQueryFilters = useCallback(
    (
      queryFilters: Partial<
        Record<
          string,
          | Omit<QueryFilter, keyof FilterIdentifier>
          | Omit<EventQueryFilter, keyof FilterIdentifier>
          | Omit<DepartmentSizeQueryFilter, keyof FilterIdentifier>
        >
      >,
    ) => {
      const updateConfiguration = (
        configuration: Configuration,
        skipUndefined: boolean,
      ): Configuration => {
        let newFilters = configuration.query.filters;

        for (const [filterId, filterIdentifier] of filterIdentifiers) {
          if (!filterIdentifier) continue;
          const newQueryFilter = queryFilters[filterId];
          if (skipUndefined && newQueryFilter === undefined) continue;
          newFilters = updateFilter(
            newFilters,
            filterIdentifier,
            newQueryFilter,
          );
        }

        return {
          ...configuration,
          query: {
            ...configuration.query,
            filters: newFilters,
          },
        };
      };
      setConfiguration((prev) => updateConfiguration(prev, false));
    },
    [filterIdentifiers, setConfiguration],
  );

  const clearFilters = useCallback(async () => {
    const clearedFilters = group.filterType.filters.reduce(
      (acc, filterId) => {
        acc[filterId] = undefined;
        return acc;
      },
      {} as Record<string, undefined>,
    );

    setQueryFilters(clearedFilters);
    isIntentionalNavRef.current = true;
    onUpdated?.();
  }, [group.filterType.filters, setQueryFilters, onUpdated]);

  // Render the appropriate component based on filter type
  const renderFilter = () => {
    function renderComponent<Group extends FilterGroup>(group: Group) {
      const { Component } = group.filterType;

      // eslint-disable-next-line @typescript-eslint/no-empty-object-type
      const props: FilterGroupComponentProps<string, {}> = {
        group,
        formId,
        queryFilters,
        configuration,
        update: async (queryFilters) => {
          setQueryFilters(queryFilters);
          isIntentionalNavRef.current = true;
          onUpdated?.();
        },
        showButtons,
        setShowButtons,
        setIsDirty,
      };

      return <Component {...props} />;
    }

    return renderComponent(group as FilterGroup);
  };

  return (
    <SettingsContainer
      backLinkOptions={hasBorder ? backLinkOptions : undefined}
      border={hasBorder}
      padding={hasBorder}
    >
      <div
        className={twMerge(
          `mx-auto flex size-full flex-col gap-y-12`,
          group.fullWidth ? "" : "max-w-2xl",
        )}
      >
        <div className="flex-1">{renderFilter()}</div>
        {showButtons && (
          <div className="mt-auto flex flex-wrap justify-center gap-4 pt-12">
            <Button size="lg" intent="secondary" link={backLinkOptions} animate>
              Tilbage
            </Button>
            <Button
              size="lg"
              type="submit"
              form={formId}
              animate
              isDisabled={!isDirty}
            >
              {isNewCampaign ? "Gem" : "Videre"}
            </Button>
            <div className="flex w-full justify-center">
              {!hideCancelButton && hasValue && (
                <Button
                  size="lg"
                  type="button"
                  intent="tertiary"
                  onPress={clearFilters}
                >
                  Fjern filter
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
      <AlertModal
        isOpen={blocker.status === "blocked"}
        title="Du har ikke-gemte ændringer"
        description="Dine ændringer vil gå tabt. Er du sikker på at du vil fortsætte?"
        onClose={blocker.reset}
        onConfirm={blocker.proceed}
      />
    </SettingsContainer>
  );
}
